import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { track } from '../lib/analytics';
import { ApiError, api } from '../lib/api';
import { haptic } from '../lib/telegram';

function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function CreateLeague() {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const slug = toSlug(name) || 'my-league';
  const nav = useNavigate();
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: () => api.createLeague({ slug, name: name.trim(), description: description.trim() || undefined }),
    onSuccess: async (res) => {
      haptic.success();
      track('league_created', { league_slug: res.league.slug, is_public: false });
      await qc.invalidateQueries({ queryKey: ['leagues', 'me'] });
      nav(`/leagues/${res.league.slug}`);
    },
    onError: () => haptic.error(),
  });

  const valid = name.trim().length >= 3 && name.trim().length <= 60;

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header>
        <h1 className="font-display text-3xl uppercase tracking-wide">Create league</h1>
        <p className="text-text-dim text-sm mt-1">
          Private league — invite-only via a deep-link.
        </p>
      </header>

      <Card className="space-y-3">
        <label className="block">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="Pub Gaffers"
            className="mt-1 w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-trophy-gold"
          />
        </label>
        <label className="block">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Description (optional)
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder="Friends from the local"
            className="mt-1 w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-trophy-gold resize-none"
          />
        </label>
        <div className="font-mono text-xs text-text-faint">slug · {slug}</div>
      </Card>

      <Button
        variant="gold"
        fullWidth
        disabled={!valid || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? 'Creating…' : 'Create league'}
      </Button>
      {create.error && create.error instanceof ApiError && create.error.status === 402 ? (
        <Card className="border-premium/40 space-y-2">
          <div className="font-chunky text-sm text-premium">Free-tier cap reached</div>
          <div className="text-xs text-text-dim">
            You've created the max 3 private leagues on the free tier. Upgrade to Premium for
            unlimited leagues.
          </div>
          <Link
            to="/settings"
            className="block text-center font-display-alt text-xs uppercase tracking-widest text-trophy-gold"
          >
            Upgrade in Settings →
          </Link>
        </Card>
      ) : create.error ? (
        <div className="text-crimson text-sm text-center">
          {create.error instanceof Error ? create.error.message : 'Failed to create'}
        </div>
      ) : null}
    </div>
  );
}
